import { KeyboardEvent, useEffect, useRef } from 'react';

import type { AssignWizardHandle, CustomTestCategory, CustomTestCreateWizardHandle, CustomTestListItem, CustomTestProduct } from 'pages/custom_tests_v1';
import { CustomTestCreateWizard } from 'pages/custom_tests_v1/components/custom_test_create_wizard/CustomTestCreateWizard';
import { CustomTestsTable } from 'pages/custom_tests_v1/components/custom_tests_search/components/CustomTestsTable';
import { SearchFilters } from 'pages/custom_tests_v1/components/custom_tests_search/components/SearchFilters';
import { CustomTestsSearchContext } from 'pages/custom_tests_v1/components/custom_tests_search/context';
import { AssignWizard } from 'pages/custom_tests_v1/components/assign_wizard/AssignWizard';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { Dialog, DialogHandle } from 'components/modal_manager';
import { useCustomTests } from 'pages/custom_tests_v1/context';
import { helpContent } from 'pages/custom_tests_v1/help';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNotification } from 'app/notifications';
import { PageHeader } from 'components/layout/page';
import { CustomTestApi } from 'apis/CustomTestApi';
import { useNavigate } from 'react-router-dom';
import { Alert, Card } from 'react-bootstrap';
import { useSuperState } from 'hooks';
import { useApp } from 'app';

export type CustomTestsSearchContextValue = {
    categories: CustomTestCategory[];
    categoryId: number;
    createdBy: string;
    fetchTests: () => Promise<void>;
    handleAssign: (testId: number, testName: string) => Promise<void>;
    handleCategoryChange: (categoryId: number) => Promise<void>;
    handleCloneConfirmation: (test: CustomTestListItem) => void;
    handleDeleteConfirmation: (testToDelete: CustomTestListItem) => void;
    handleSearchKeyDown: (e: KeyboardEvent) => void;
    handleToggleStatus: (id: number) => Promise<boolean>,
    loading: boolean;
    minScore: string;
    modeId: number;
    productId: number;
    products: CustomTestProduct[]
    setCreatedBy: (createdBy: string) => void;
    setMinScore: (minScore: string) => void;
    setModeId: (modeId: number) => void;
    setProductId: (productId: number) => void;
    setStatusId: (statusId: number) => void;
    setTestName: (testName: string) => void;
    statusId: number;
    testName: string;
    tests: CustomTestListItem[];
};

export const CustomTestsSearch = () => {

    const [state, setState] = useSuperState({
        categories: [] as CustomTestCategory[],
        categoryId: 0,
        createdBy: '',
        loading: false,
        minScore: '',
        modeId: 0,
        productId: 0,
        products: [] as CustomTestProduct[],
        statusId: -1,
        testName: '',
        tests: [] as CustomTestListItem[]
    });
    const { categories, categoryId, createdBy, loading, minScore, modeId, productId, products, statusId, testName, tests } = state;

    const { isFeatureAllowed, selectedPartner } = useApp();
    const { formatMessage } = useIntl();
    const { permissions } = useCustomTests();
    const { createToast } = useNotification();
    const navigate = useNavigate();

    const wizardRef = useRef<CustomTestCreateWizardHandle>(null);
    const assignWizardRef = useRef<AssignWizardHandle>(null);
    const dialogRef = useRef<DialogHandle>();
    const hasHandledCreateQueryRef = useRef(false);

    const setTestName = (testName: string) => setState({ testName });

    const setCreatedBy = (createdBy: string) => setState({ createdBy });

    const setMinScore = (minScore: string) => setState({ minScore });

    const setStatusId = (statusId: number) => setState({ statusId });

    const setProductId = (productId: number) => setState({ productId });

    const setModeId = (modeId: number) => setState({ modeId });

    const fetchCategories = async () => {

        try {

            const categories = await CustomTestApi.GetCategories(selectedPartner.id);
            setState({ categories: categories ?? [] });

        } catch (e) {

            console.error(e);

        }

    };

    const fetchTests = async () => {

        try {

            setState({
                loading: true,
                tests: []
            });

            const results = await CustomTestApi.Search({
                categoryId: state?.categoryId || undefined,
                minScore: state.minScore !== '' ? parseInt(state.minScore) : undefined,
                modeId: state.modeId || undefined,
                productId: state.productId || undefined,
                statusId: state.statusId >= 0 ? state.statusId : undefined,
                testName: state.testName || undefined
            });

            const filtered = state.createdBy
                ? (results ?? []).filter(test => test.CreatedBy.toLowerCase().includes(state.createdBy.toLowerCase()))
                : (results ?? []);

            setState({
                loading: false,
                tests: filtered.map(test => ({ ...test, Id: test.Id }))
            });

        } catch (e: any) {

            setState({ loading: false });
            createToast(e?.message ?? (formatMessage({ id: 'failed_to_load_tests.ef0018f2', defaultMessage: 'Failed to load tests.' })), 'danger');

        }

    };

    const handleClone = async (test: CustomTestListItem) => {

        try {

            await CustomTestApi.Clone(test.Id);
            createToast(
                (formatMessage({ id: 'name_cloned_successfully.15bb85da', defaultMessage: '"{name}" cloned successfully' }, { name: test.Name })),
                'success',
                5000
            );

            fetchTests();

        } catch (e: any) {

            createToast(e?.message ?? (formatMessage({ id: 'failed_to_clone_test.b71fbe6f', defaultMessage: 'Failed to clone test.' })), 'danger');

        }

    };

    const handleCloneConfirmation = (test: CustomTestListItem) => {

        const dialog = dialogRef?.current;
        if (!dialog) return;

        dialog.show({
            actions: [
                dialog.CancelAction(),
                dialog.OKAction({
                    label: formatMessage({ id: 'clone.747ba0bb', defaultMessage: 'Clone' }),
                    onClick: () => handleClone(test)
                })
            ],
            body: (
                <p>
                    <FormattedMessage defaultMessage="Are you sure you want to clone" id="are_you_sure_you_want_to_clone.28c0a718" />
                    {' '}
                    <strong>
                        {test.Name}
                    </strong>
                    ?
                </p>
            ),
            title: formatMessage({ id: 'confirm_clone.cf301d76', defaultMessage: 'Confirm Clone' })
        });

    };

    const handleDelete = async (test: CustomTestListItem) => {

        try {

            await CustomTestApi.Delete(test.Id);
            createToast(
                (formatMessage({ id: 'name_deleted.44059d79', defaultMessage: '"{name}" deleted' }, { name: test.Name })),
                'success',
                5000
            );

            fetchTests();

        } catch (e: any) {

            console.error(e);
            createToast(e?.message ?? (formatMessage({ id: 'failed_to_delete_test.487f6e2e', defaultMessage: 'Failed to delete test.' })), 'danger');

        }

    };

    const handleDeleteConfirmation = (test: CustomTestListItem) => {

        const dialog = dialogRef?.current;
        if (!dialog) return;

        dialog.show({
            actions: [
                dialog.CancelAction(),
                dialog.OKAction({
                    label: formatMessage({ id: 'delete.2d32914e', defaultMessage: 'Delete' }),
                    onClick: () => handleDelete(test)
                })
            ],
            body: (
                <p>
                    <FormattedMessage defaultMessage="Are you sure you want to delete" id="are_you_sure_you_want_to_delete.aaa0dee1" />
                    {' '}
                    <strong>
                        {test.Name}
                    </strong>
                    ?
                </p>
            ),
            title: formatMessage({ id: 'confirm_delete.5084e972', defaultMessage: 'Confirm Delete' })
        });

    };

    const handleAssign = async (testId: number, testName: string) => {

        const wizard = assignWizardRef?.current;
        if (!wizard) return;

        const assigned = await wizard.show({ testId, testName });

        if (assigned) {

            createToast(
                formatMessage({ id: 'assignments_saved_successfully.22d8fe61', defaultMessage: 'Assignments saved successfully.' }),
                'success',
                5000
            );

        }

    };

    const handleSearchKeyDown = (e: KeyboardEvent) => {

        if (e.key !== 'Enter') return;
        fetchTests();

    };

    const handleCategoryChange = async (categoryId: number) => {

        setState({
            categoryId,
            productId: 0,
            products: []
        });

        if (categoryId > 0) {

            try {

                const products = await CustomTestApi.GetProductsByCategory(categoryId, selectedPartner.id);
                setState({ products: products ?? [] });

            } catch (e) {

                console.error(e);

            }

        }

    };

    const handleToggleStatus = async (id: number) => {

        try {

            // The proc reports handled failures via Done=false in a 200 response; ApiCaller does not throw for those.
            const result = await CustomTestApi.ToggleActive(id);

            if (!result?.Done) {

                createToast(result?.Message || formatMessage({ id: 'failed_to_update_test_status.24e52dc7', defaultMessage: 'Failed to update test status.' }), 'danger');
                return false;

            }

            createToast(formatMessage({ id: 'test_status_updated_successfully.c7bc2a65', defaultMessage: 'Test status updated successfully' }), 'success', 5000);

            return true;

        } catch (e: any){

            console.error(e);
            createToast(e?.message ?? formatMessage({ id: 'failed_to_update_test_status.24e52dc7', defaultMessage: 'Failed to update test status.' }), 'danger');
            return false;

        }

    };

    const handleShowWizard = () => {

        const wizard = wizardRef?.current;
        if (!wizard) return;

        wizard.show();

    };

    useEffect(() => {

        fetchCategories();
        fetchTests();

    }, []);

    useEffect(() => {

        if (hasHandledCreateQueryRef.current || !permissions.CanCreate) return;

        const searchParams = new URLSearchParams(window.location.search);
        const createValue = (searchParams.get('create') ?? '').toLowerCase();

        if (createValue != 'true') return;

        hasHandledCreateQueryRef.current = true;
        wizardRef.current?.show();

    }, [permissions.CanCreate]);

    const context: CustomTestsSearchContextValue = {
        categories,
        categoryId,
        createdBy,
        fetchTests,
        handleAssign,
        handleCategoryChange,
        handleCloneConfirmation,
        handleDeleteConfirmation,
        handleSearchKeyDown,
        handleToggleStatus,
        loading,
        minScore,
        modeId,
        productId,
        products,
        setCreatedBy,
        setMinScore,
        setModeId,
        setProductId,
        setStatusId,
        setTestName,
        statusId,
        testName,
        tests
    };

    return (
        <CustomTestsSearchContext.Provider value={context}>
            <div className={'custom-tests-search d-flex flex-column gap-3'}>

                {/* Invitation to the rebuilt Custom Tests experience, which lives at its own URL.
                    Ring-gated (flag rides the "In Development" ring) so the new version is only
                    advertised to inner-ring users while it bakes. */}
                {isFeatureAllowed('Admin.CustomTest.V2Banner') && (
                    <Alert className={'d-flex align-items-center justify-content-between flex-wrap gap-2 mb-0'} variant={'info'}>
                        <div>
                            <i className={'fa-solid fa-wand-magic-sparkles me-2'} />
                            <strong>
                                <FormattedMessage defaultMessage="A new version of Custom Tests is available." id="a_new_version_of_custom_tests_is_availab.9d923df2" />
                            </strong>
                            {' '}
                            <FormattedMessage defaultMessage="Redesigned lookup, details, and test builder." id="redesigned_lookup_details_and_test_build.a3e184fc" />
                        </div>
                        <GMetrixButton
                            iconClass={'fa-solid fa-arrow-right'}
                            onClick={() => navigate('/custom-tests-v2')}
                            variant={'primary'}
                        >
                            <FormattedMessage defaultMessage="Try the new version" id="try_the_new_version.bf9e7686" />
                        </GMetrixButton>
                    </Alert>
                )}

                <PageHeader
                    cta={{
                        disabled: !permissions.CanCreate || loading,
                        hidden: !permissions.CanCreate,
                        label: (
                            <div className={'d-inline-flex align-items-center gap-1'}>
                                <i className={'fa-solid fa-plus'} />
                                <span>
                                    <FormattedMessage defaultMessage="Create Test" id="create_test.c699ee0a" />
                                </span>
                            </div>
                        ),
                        onClick: handleShowWizard
                    }}
                    helpContent={helpContent}
                    title={formatMessage({ id: 'custom_tests.9e7494ea', defaultMessage: 'Custom Tests' })}
                />

                <Card body>
                    <SearchFilters />
                    <CustomTestsTable />
                </Card>

                <Dialog ref={dialogRef} />

                <CustomTestCreateWizard
                    onSuccess={testId => testId > 0 ? navigate(`/custom-tests/edit?testId=${testId}`) : fetchTests()}
                    ref={wizardRef}
                />

                <AssignWizard ref={assignWizardRef} />

            </div>
        </CustomTestsSearchContext.Provider>
    );

};
